import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	BanknotesIcon,
	CheckCircleIcon,
	ClockIcon,
	CurrencyDollarIcon,
	ArrowTrendingUpIcon,
	ArrowTrendingDownIcon,
	FunnelIcon,
	XMarkIcon,
	ChevronDownIcon,
	TrophyIcon,
	UsersIcon,
	ArrowRightIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Spinner } from '../../components/ui';
import { revenueApi } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { RevenuePeriod, RevenueResponse, MonthlyRevenuePoint } from '../../types/revenue';

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => '$' + Math.round(amount).toLocaleString('en-US');

const formatCurrencyCompact = (amount: number): string => {
	if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
	return `$${Math.round(amount)}`;
};

const formatPct = (pct: number, digits = 1): string =>
	`${pct >= 0 ? '' : ''}${pct.toFixed(digits)}%`;

const getInitials = (name: string): string =>
	name
		.split(' ')
		.map(n => n[0])
		.filter(Boolean)
		.slice(0, 2)
		.join('')
		.toUpperCase();

// ─── Period Selector ────────────────────────────────────────────

const PERIODS: { key: RevenuePeriod; label: string }[] = [
	{ key: 'WEEK', label: 'This Week' },
	{ key: 'MONTH', label: 'This Month' },
	{ key: 'QUARTER', label: 'Quarter' },
	{ key: 'YEAR', label: 'Year' },
];

// ─── Stat Card ──────────────────────────────────────────────────

type StatTone = 'purple' | 'green' | 'gold' | 'blue';

const TONE_ACCENT: Record<StatTone, string> = {
	purple: 'from-indigo-500 to-purple-600',
	green: 'from-emerald-500 to-emerald-600',
	gold: 'from-amber-500 to-amber-600',
	blue: 'from-blue-500 to-blue-600',
};

const TONE_VALUE_COLOR: Record<StatTone, string> = {
	purple: 'text-gray-900 dark:text-white',
	green: 'text-emerald-600 dark:text-emerald-400',
	gold: 'text-amber-600 dark:text-amber-400',
	blue: 'text-blue-600 dark:text-blue-400',
};

const StatCard = ({
	tone,
	icon,
	label,
	value,
	sub,
	subDirection,
}: {
	tone: StatTone;
	icon: ReactNode;
	label: string;
	value: string;
	sub: ReactNode;
	subDirection?: 'up' | 'down' | 'neutral';
}) => {
	const subColor =
		subDirection === 'up'
			? 'text-emerald-600 dark:text-emerald-400'
			: subDirection === 'down'
				? 'text-red-500 dark:text-red-400'
				: 'text-gray-400 dark:text-gray-500';

	return (
		<div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm hover:shadow-md transition-shadow">
			<div
				className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${TONE_ACCENT[tone]}`}
			/>
			<div className="text-gray-400 dark:text-gray-500 mb-2">{icon}</div>
			<div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
				{label}
			</div>
			<div className={`text-2xl font-extrabold leading-none mb-2 ${TONE_VALUE_COLOR[tone]}`}>
				{value}
			</div>
			<div className={`text-xs font-semibold ${subColor}`}>{sub}</div>
		</div>
	);
};

// ─── Monthly Bar Chart ──────────────────────────────────────────

const MonthlyRevenueChart = ({ data }: { data: MonthlyRevenuePoint[] }) => {
	const max = useMemo(() => Math.max(1, ...data.map(d => d.revenue)), [data]);
	const current = data.find(d => d.current);

	return (
		<div>
			<div className="flex items-end gap-2.5 h-40 px-1">
				{data.map((d, i) => {
					const heightPct = Math.max(2, Math.round((d.revenue / max) * 100));
					return (
						<div
							key={`${d.year}-${d.month}-${i}`}
							className="flex-1 flex flex-col items-center gap-1.5 h-full"
						>
							<div className="w-full flex-1 flex items-end bg-gray-100 dark:bg-gray-700/50 rounded-t-md relative">
								<div
									className={`w-full rounded-t-md relative transition-all duration-500 ${
										d.current
											? 'bg-gradient-to-b from-emerald-500 to-emerald-600'
											: 'bg-gradient-to-b from-indigo-500 to-purple-600'
									}`}
									style={{ height: `${heightPct}%` }}
								>
									<span
										className={`absolute -top-5 left-1/2 -translate-x-1/2 text-[0.65rem] font-bold whitespace-nowrap ${
											d.current
												? 'text-emerald-600 dark:text-emerald-400'
												: 'text-indigo-600 dark:text-indigo-400'
										}`}
									>
										{formatCurrencyCompact(d.revenue)}
									</span>
								</div>
							</div>
							<div className="text-[0.7rem] font-semibold text-gray-400 dark:text-gray-500">
								{d.month}
							</div>
						</div>
					);
				})}
				{data.length === 0 && (
					<div className="flex-1 text-center text-sm text-gray-400 dark:text-gray-500 self-center">
						No revenue data
					</div>
				)}
			</div>

			{/* Legend */}
			<div className="flex gap-4 mt-4 text-xs text-gray-500 dark:text-gray-400">
				<span className="inline-flex items-center gap-1.5">
					<span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-indigo-500 to-purple-600" />
					Previous months
				</span>
				<span className="inline-flex items-center gap-1.5">
					<span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-emerald-500 to-emerald-600" />
					Current {current ? `(${current.month} ${current.year})` : 'month'}
				</span>
			</div>
		</div>
	);
};

// ─── Section Card ───────────────────────────────────────────────

const SectionCard = ({
	title,
	icon,
	action,
	children,
	bodyClassName,
}: {
	title: string;
	icon?: ReactNode;
	action?: ReactNode;
	children: ReactNode;
	bodyClassName?: string;
}) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
		<div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
			<div className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
				{icon}
				<span>{title}</span>
			</div>
			{action}
		</div>
		<div className={bodyClassName ?? 'px-5 py-5'}>{children}</div>
	</div>
);

// ─── Main Component ─────────────────────────────────────────────

const Revenue = () => {
	const navigate = useNavigate();
	const { selectedLocationId } = useProviderProfileStore();

	// Applied filter state (what is actually being queried)
	const [period, setPeriod] = useState<RevenuePeriod>('MONTH');
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [staffId, setStaffId] = useState<string>('');
	const [service, setService] = useState<string>('');

	// Draft filter state (in the collapsible panel before Apply)
	const [draftDateFrom, setDraftDateFrom] = useState('');
	const [draftDateTo, setDraftDateTo] = useState('');
	const [draftStaffId, setDraftStaffId] = useState('');
	const [draftService, setDraftService] = useState('');

	const [filterOpen, setFilterOpen] = useState(false);
	const [data, setData] = useState<RevenueResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const usingCustomDateRange = Boolean(dateFrom || dateTo);
	const appliedFilterCount =
		(dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (staffId ? 1 : 0) + (service ? 1 : 0);

	const fetchRevenue = useCallback(async () => {
		if (!selectedLocationId) return;
		try {
			setIsLoading(true);
			setError(null);
			const response = await revenueApi.getRevenue({
				locationId: selectedLocationId,
				period: usingCustomDateRange ? undefined : period,
				dateFrom: dateFrom || undefined,
				dateTo: dateTo || undefined,
				staffId: staffId ? Number(staffId) : undefined,
				service: service || undefined,
			});
			setData(response);
		} catch {
			setError('Failed to load revenue. Please try again.');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, period, dateFrom, dateTo, staffId, service, usingCustomDateRange]);

	useEffect(() => {
		fetchRevenue();
	}, [fetchRevenue]);

	const handleSelectPeriod = (next: RevenuePeriod) => {
		setPeriod(next);
	};

	const handleApplyFilters = () => {
		setDateFrom(draftDateFrom);
		setDateTo(draftDateTo);
		setStaffId(draftStaffId);
		setService(draftService);
		setFilterOpen(false);
	};

	const handleClearFilters = () => {
		setDraftDateFrom('');
		setDraftDateTo('');
		setDraftStaffId('');
		setDraftService('');
		setDateFrom('');
		setDateTo('');
		setStaffId('');
		setService('');
	};

	const handleGoToStaff = () => {
		navigate('/providers/staff-management');
	};

	// ─── Render states ─────────────────────────────────────────

	if (isLoading && !data) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			</AppLayout>
		);
	}

	if (error && !data) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<button
						onClick={fetchRevenue}
						className="px-4 py-2 rounded-lg font-semibold text-sm text-white"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						Retry
					</button>
				</div>
			</AppLayout>
		);
	}

	const availableStaff = data?.availableStaff ?? [];
	const availableServices = data?.availableServices ?? [];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">Revenue</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Track your salon's income and financial performance
				</p>
			</div>

			{/* Toolbar: heading + period selector */}
			<div className="flex flex-wrap items-center justify-between gap-3 mb-5">
				<div className="text-base font-bold text-gray-900 dark:text-white">
					Financial Overview
				</div>
				<div className="flex flex-wrap gap-1.5">
					{PERIODS.map(p => {
						const isActive = !usingCustomDateRange && p.key === period;
						return (
							<button
								key={p.key}
								onClick={() => handleSelectPeriod(p.key)}
								className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all ${
									isActive
										? 'text-white border-transparent shadow-sm'
										: 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400'
								}`}
								style={
									isActive
										? {
												background:
													'linear-gradient(135deg, #667eea, #764ba2)',
											}
										: undefined
								}
							>
								{p.label}
							</button>
						);
					})}
				</div>
			</div>

			{/* Filter Panel */}
			<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-5">
				<button
					type="button"
					onClick={() => setFilterOpen(o => !o)}
					className="w-full flex items-center justify-between px-5 py-3.5"
				>
					<div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-200">
						<FunnelIcon className="w-4 h-4" />
						Customise Filters
						{appliedFilterCount > 0 && (
							<span
								className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[0.65rem] font-extrabold text-white"
								style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
							>
								{appliedFilterCount}
							</span>
						)}
					</div>
					<div className="flex items-center gap-3">
						{appliedFilterCount > 0 && (
							<span
								onClick={e => {
									e.stopPropagation();
									handleClearFilters();
								}}
								className="text-xs font-semibold text-red-500 hover:text-red-600 cursor-pointer"
							>
								Clear all
							</span>
						)}
						<ChevronDownIcon
							className={`w-4 h-4 text-gray-400 transition-transform ${filterOpen ? 'rotate-180' : ''}`}
						/>
					</div>
				</button>

				{filterOpen && (
					<div className="border-t border-gray-100 dark:border-gray-700 px-5 py-4 flex flex-wrap items-end gap-4">
						<FilterField label="Date From">
							<input
								type="date"
								value={draftDateFrom}
								onChange={e => setDraftDateFrom(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							/>
						</FilterField>
						<FilterField label="Date To">
							<input
								type="date"
								value={draftDateTo}
								onChange={e => setDraftDateTo(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							/>
						</FilterField>
						<FilterField label="Staff Member">
							<select
								value={draftStaffId}
								onChange={e => setDraftStaffId(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							>
								<option value="">All Staff</option>
								{availableStaff.map(s => (
									<option key={s.id} value={s.id}>
										{s.name}
									</option>
								))}
							</select>
						</FilterField>
						<FilterField label="Service">
							<select
								value={draftService}
								onChange={e => setDraftService(e.target.value)}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
							>
								<option value="">All Services</option>
								{availableServices.map(s => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</select>
						</FilterField>
						<button
							type="button"
							onClick={handleApplyFilters}
							className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 whitespace-nowrap"
							style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
						>
							Apply Filters
						</button>
					</div>
				)}

				{appliedFilterCount > 0 && (
					<div className="border-t border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-5 py-2 flex items-center justify-between text-xs font-semibold text-blue-700 dark:text-blue-300">
						<span>
							Filtered by:{' '}
							{[
								dateFrom && `From ${dateFrom}`,
								dateTo && `To ${dateTo}`,
								staffId && availableStaff.find(s => String(s.id) === staffId)?.name,
								service,
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
						<button
							onClick={handleClearFilters}
							className="inline-flex items-center gap-1 underline hover:no-underline"
						>
							<XMarkIcon className="w-3 h-3" /> Clear
						</button>
					</div>
				)}
			</div>

			{data && (
				<>
					{/* Stat Cards */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
						<StatCard
							tone="purple"
							icon={<CurrencyDollarIcon className="w-6 h-6" />}
							label="Total Revenue"
							value={formatCurrency(data.totalRevenue)}
							sub={
								data.totalRevenueChangePct != null ? (
									<span className="inline-flex items-center gap-1">
										{data.totalRevenueChangePct >= 0 ? (
											<ArrowTrendingUpIcon className="w-3.5 h-3.5" />
										) : (
											<ArrowTrendingDownIcon className="w-3.5 h-3.5" />
										)}
										{formatPct(data.totalRevenueChangePct)} vs last period
									</span>
								) : (
									'—'
								)
							}
							subDirection={
								data.totalRevenueChangePct == null
									? 'neutral'
									: data.totalRevenueChangePct >= 0
										? 'up'
										: 'down'
							}
						/>
						<StatCard
							tone="green"
							icon={<CheckCircleIcon className="w-6 h-6" />}
							label="Collected"
							value={formatCurrency(data.collected)}
							sub={
								data.collectionRate != null
									? `${data.collectionRate.toFixed(1)}% collection rate`
									: 'Collection rate unavailable'
							}
							subDirection="neutral"
						/>
						<StatCard
							tone="gold"
							icon={<ClockIcon className="w-6 h-6" />}
							label="Pending"
							value={formatCurrency(data.pending)}
							sub={`${data.pendingCount} invoice${data.pendingCount === 1 ? '' : 's'} outstanding`}
							subDirection="neutral"
						/>
						<StatCard
							tone="blue"
							icon={<BanknotesIcon className="w-6 h-6" />}
							label="Tips Collected"
							value={formatCurrency(data.tipsCollected)}
							sub={
								data.tipsChangePct != null ? (
									<span className="inline-flex items-center gap-1">
										{data.tipsChangePct >= 0 ? (
											<ArrowTrendingUpIcon className="w-3.5 h-3.5" />
										) : (
											<ArrowTrendingDownIcon className="w-3.5 h-3.5" />
										)}
										{formatPct(data.tipsChangePct)} vs last period
									</span>
								) : (
									'—'
								)
							}
							subDirection={
								data.tipsChangePct == null
									? 'neutral'
									: data.tipsChangePct >= 0
										? 'up'
										: 'down'
							}
						/>
					</div>

					{/* Chart + Top Services */}
					<div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 mb-5">
						<SectionCard
							title="Monthly Revenue"
							icon={<ArrowTrendingUpIcon className="w-4 h-4 text-indigo-500" />}
							action={
								data.monthlyRevenue.find(m => m.current) && (
									<span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
										{data.monthlyRevenue.find(m => m.current)?.month}{' '}
										{data.monthlyRevenue.find(m => m.current)?.year}
									</span>
								)
							}
						>
							<MonthlyRevenueChart data={data.monthlyRevenue} />
						</SectionCard>

						<SectionCard
							title="Top Services"
							icon={<TrophyIcon className="w-4 h-4 text-amber-500" />}
							bodyClassName="px-5 py-2"
						>
							{data.topServices.length === 0 ? (
								<div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
									No service revenue yet
								</div>
							) : (
								data.topServices.map((s, i) => (
									<div
										key={`${s.name}-${i}`}
										className="flex items-center gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
									>
										<div
											className="min-w-[22px] rounded-md flex items-center justify-center text-[0.65rem] font-extrabold text-white"
											style={{
												background:
													'linear-gradient(135deg, #667eea, #764ba2)',
												width: 22,
												height: 22,
											}}
										>
											{i + 1}
										</div>
										<div className="flex-1 text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">
											{s.name}
										</div>
										<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
											{formatCurrency(s.revenue)}
										</div>
									</div>
								))
							)}
						</SectionCard>
					</div>

					{/* Revenue by Staff */}
					<SectionCard
						title="Revenue by Staff"
						icon={<UsersIcon className="w-4 h-4 text-indigo-500" />}
						bodyClassName="px-5 py-2"
					>
						{data.revenueByStaff.length === 0 ? (
							<div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
								No staff revenue for this period
							</div>
						) : (
							data.revenueByStaff.map(s => (
								<button
									key={s.staffId}
									type="button"
									onClick={handleGoToStaff}
									title={`View ${s.name} in Staff Management`}
									className="w-full flex items-center gap-3.5 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg px-2 -mx-2 transition-colors"
								>
									<div
										className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
										style={{
											background: 'linear-gradient(135deg, #667eea, #764ba2)',
										}}
									>
										{getInitials(s.name)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 underline underline-offset-2 truncate">
											{s.name}
										</div>
										<div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
											{s.role} · {s.appointmentCount} appointment
											{s.appointmentCount === 1 ? '' : 's'}
										</div>
									</div>
									<div className="hidden sm:block flex-1 max-w-[180px] h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
										<div
											className="h-full rounded-full"
											style={{
												width: `${Math.max(2, Math.min(100, s.pctOfTop))}%`,
												background:
													'linear-gradient(90deg, #667eea, #764ba2)',
											}}
										/>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 min-w-[70px] text-right">
											{formatCurrency(s.revenue)}
										</div>
										<ArrowRightIcon className="w-4 h-4 text-indigo-500" />
									</div>
								</button>
							))
						)}
					</SectionCard>
				</>
			)}
		</AppLayout>
	);
};

// ─── Filter Field ───────────────────────────────────────────────

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
	<div className="flex flex-col gap-1 flex-1 min-w-[160px]">
		<label className="text-[0.7rem] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
			{label}
		</label>
		{children}
	</div>
);

export default Revenue;
